import { useCallback, useRef, useState } from "react";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { useDispatch } from "react-redux";

import LockIcon from "@material-ui/icons/Lock";
import classnames from "classnames";
import { IS_CROSS_PLOT_CHART } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { checkUserMinDateNotGreaterThanSeriesMax } from "entities/charts/eva/locks/utils/checkUserMinDateNotGreaterThanSeriesMax";
import { updateChartByPath } from "store/features";
import styled from "styled-components/macro";
import { LockState } from "types/charts/locks";
import { convertQuarterToMonth } from "utils/convertQuarterToMonth";

import { DataEnum, Format } from "models";

import { ToastType, dismissChartToast, showChartToast } from "../../ChartToast";
import "./AxisInput.scss";

// Regex pattern for validating date input (allows only numbers and hyphens)
export const DATE_INPUT_REGEX = /^[0-9-]*$/;
export const YEAR_QUARTER_DATE_INPUT_REGEX = /^[0-9Q-]*$/;

// Default placeholder for date inputs
export const YEAR_DATE_INPUT = "YYYY";
export const MONTH_DATE_INPUT = "YYYY-MM";
export const YEAR_QUARTER_DATE_INPUT = "YYYY-QQ";
export const DAY_MONTH_DATE_INPUT = "YYYY-MM-DD";
const minLockedDateWarning =
  "Min lock was set beyond series max, no lock has been applied";

export function AxisInput({
  className = "",
  disabled = false,
  axisInput = { value: "0", type: DataEnum.Number } as LockState,
  dataRange = "",
  lockPath = undefined as string | undefined,
  inputFormat = "number",
  type = "Number",
  step = "",
  min = "",
  max = "",
  placeholder = "0",
  setShowInput = null,
  chartType = undefined,
  unit = "",
  series = [],
  ...rest
}) {
  const isTotalRateDateChart =
    chartType === EvaChart.TotalRateDate || chartType === EvaChart.TotalRateDateMidstream;
  const isRateDateChart =
    chartType === EvaChart.RateDate || chartType === EvaChart.RateDateMidstream;
  const isDateChart =
    chartType === EvaChart.CAGR ||
    chartType === EvaChart.DeclineRate ||
    chartType === EvaChart.InjectorTotal ||
    isRateDateChart ||
    isTotalRateDateChart;

  const isCrossPlotDayFormat =
    IS_CROSS_PLOT_CHART(chartType) && axisInput.type === DataEnum.Date;
  const isCrossPlotQuarterFormat =
    IS_CROSS_PLOT_CHART(chartType) && axisInput.format === Format.YearQuarter;
  const isCrossPlotMonthFormat =
    IS_CROSS_PLOT_CHART(chartType) && axisInput.format === Format.Month;

  // A typed value is a user lock; saving it as DefaultDate would get it recomputed away
  const savedType =
    axisInput.type === DataEnum.DefaultDate ? DataEnum.Date : axisInput.type;

  const dateInputRef = useRef(null);
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const [currValue, setCurrValue] = useState(() => initializeCurrValue());

  const temporaryValueRef = useRef(null);

  // For YearQuarter format, convert to month for the Datetime picker's initial view
  const getDatetimeValue = () => {
    if (inputFormat === Format.YearQuarter && typeof currValue === "string") {
      const converted = convertQuarterToMonth(currValue);
      // Return YYYY-MM format for the Datetime month picker
      return converted.slice(0, 7);
    }
    return currValue;
  };

  function initializeCurrValue() {
    if (inputFormat === Format.Number) {
      if (typeof axisInput.value === "string") {
        return Number(axisInput.value.replace(/,/g, ""));
      }
      if (typeof axisInput.value === "number") {
        return axisInput.value;
      }
    } else if (inputFormat === Format.Year) {
      return axisInput.value?.toString().slice(0, 4);
    } else if (inputFormat === Format.Month) {
      return axisInput.value?.toString().slice(0, 7);
    } else if (inputFormat === Format.Day) {
      return axisInput.value?.toString();
    }
    return axisInput.value?.toString();
  }

  const resolvedLockPath = lockPath ?? `locks.${dataRange}`;

  function handleHide() {
    const tempValue = temporaryValueRef.current;
    if (tempValue && tempValue !== currValue) {
      setCurrValue(tempValue);
      dispatch(
        updateChartByPath({
          id,
          path: resolvedLockPath,
          value: { value: tempValue, type: savedType }
        })
      );
    }
    setShowInput(false);
  }

  function handleBlur(e) {
    if (e.currentTarget === e.target) {
      const initialValue = initializeCurrValue();
      const hasChanged = currValue !== initialValue;
      const hasContent =
        currValue !== "" && currValue !== null && currValue !== undefined;
      if (hasChanged && hasContent) {
        handleSave();
      } else {
        setShowInput(false);
      }
    }
  }

  function handleChange(evt) {
    // If it's a date input and value is empty, show the placeholder
    if (inputFormat !== Format.Number && evt.target.value === "") {
      setCurrValue("");
    } else {
      setCurrValue(evt.target.value);
    }
  }

  function handleSave() {
    const valueToSave = convertQuarterToMonth(currValue.toString());
    if (valueToSave !== currValue) {
      setCurrValue(valueToSave);
    }

    // Validate min date is not greater than max date in series for xMin on date charts
    if (dataRange === "xMin" && isDateChart && series) {
      const isUserMinGreaterThanSeriesMax = checkUserMinDateNotGreaterThanSeriesMax(
        valueToSave,
        series
      );
      if (isUserMinGreaterThanSeriesMax === undefined) {
        const userMinOverSeriesMaxWarning = `user-min-greater-than-series-max-${id}`;
        dismissChartToast(id, userMinOverSeriesMaxWarning);
        showChartToast(id, minLockedDateWarning, ToastType.Warning, {
          toastId: userMinOverSeriesMaxWarning
        });
        setShowInput(false);
        return;
      }
    }

    dispatch(
      updateChartByPath({
        id,
        path: resolvedLockPath,
        value: {
          value: valueToSave,
          type: savedType
        }
      })
    );

    setShowInput(false);
  }

  function handleKeypress(event) {
    if (event.key === "Enter") {
      handleSave();
    }
    if (event.key === "Escape") {
      setShowInput(false);
    }
  }

  function handleDatetimeChange(value) {
    const formattedValue =
      typeof value === "object"
        ? isCrossPlotDayFormat
          ? value.format(DAY_MONTH_DATE_INPUT)
          : isDateChart || isCrossPlotQuarterFormat || isCrossPlotMonthFormat
          ? value.format(MONTH_DATE_INPUT)
          : value.format(YEAR_DATE_INPUT)
        : value;
    if (formattedValue === value) {
      setShowInput(false);
      return; // don't update if the value is invalid
    }

    // Validate min date is not greater than max date in series for xMin on date charts
    if (dataRange === "xMin" && isDateChart && series) {
      const isUserMinGreaterThanSeriesMax = checkUserMinDateNotGreaterThanSeriesMax(
        formattedValue,
        series
      );
      if (isUserMinGreaterThanSeriesMax === undefined) {
        const userMinOverSeriesMaxWarning = `user-min-greater-than-series-max-${id}`;
        dismissChartToast(id, userMinOverSeriesMaxWarning);
        showChartToast(id, minLockedDateWarning, ToastType.Warning, {
          toastId: userMinOverSeriesMaxWarning
        });
        setShowInput(false);
        return;
      }
    }

    setCurrValue(formattedValue);
    dispatch(
      updateChartByPath({
        id,
        path: resolvedLockPath,
        value: {
          value: formattedValue,
          type: savedType
        }
      })
    );

    setShowInput(false);
  }

  const innerRef = useCallback((input) => {
    if (input !== null) {
      input.select();
    }
  }, []);

  //DateInput is to disable input box when DateTime is used
  const wrapperClassnames = classnames("axis-input", className, axisInput?.type, {
    DateInput: inputFormat !== Format.Number,
    DayDateInput: isCrossPlotDayFormat
  });
  return (
    <Wrapper
      className={wrapperClassnames}
      data-testid="axis-input-wrapper"
      $hasUnit={!!unit}>
      <LockIcon data-testid="axis-lock-icon" onClick={handleSave} />
      {inputFormat === Format.Number ? (
        <>
          <input
            className="input"
            data-testid="axis-integer-input"
            ref={innerRef}
            tabIndex={disabled ? -1 : 0}
            value={currValue}
            placeholder={placeholder}
            type={type}
            step={step}
            id={id}
            min={min}
            max={max}
            onBlur={handleBlur}
            onKeyDown={handleKeypress}
            onChange={handleChange}
            {...rest}
          />
          {unit && <UnitLabel>{unit}</UnitLabel>}
        </>
      ) : (
        <>
          <Datetime
            inputProps={{
              autoFocus: true,
              className: "input",
              ref: dateInputRef,
              onKeyDown: handleKeypress
            }}
            data-testid="axis-date-input"
            dateFormat={inputFormat === Format.YearQuarter ? Format.Month : inputFormat}
            timeFormat={false}
            value={getDatetimeValue() as string | Date}
            onChange={handleDatetimeChange}
            onClose={handleHide}
            onBeforeNavigate={(nextView, currentView, viewDate) => {
              if (currentView && viewDate) {
                if (currentView === "years" && nextView === "months") {
                  temporaryValueRef.current = `${viewDate.year()}`;
                } else if (currentView === "months" && nextView === "days") {
                  const month = (viewDate.month() + 1).toString().padStart(2, "0");
                  temporaryValueRef.current = `${viewDate.year()}-${month}`;
                }
              }
              return nextView;
            }}
          />
          <DateInputWrapper>
            <input
              className="input"
              data-testid="axis-date-text-input"
              ref={innerRef}
              tabIndex={disabled ? -1 : 0}
              value={currValue}
              placeholder={
                isCrossPlotDayFormat
                  ? DAY_MONTH_DATE_INPUT
                  : isDateChart || isCrossPlotMonthFormat
                  ? MONTH_DATE_INPUT
                  : isCrossPlotQuarterFormat
                  ? YEAR_QUARTER_DATE_INPUT
                  : YEAR_DATE_INPUT
              }
              type="text"
              id={id}
              onKeyDown={handleKeypress}
              onChange={(e) => {
                // Only allow numbers and hyphens, Also allow 'Q' for quarter format
                const value = e.target.value;
                if (isCrossPlotQuarterFormat) {
                  if (YEAR_QUARTER_DATE_INPUT_REGEX.test(value) || value === "") {
                    handleChange(e);
                  }
                } else {
                  if (DATE_INPUT_REGEX.test(value) || value === "") {
                    handleChange(e);
                  }
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              {...rest}
            />
          </DateInputWrapper>
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $hasUnit?: boolean }>`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 3px;
  width: 100px;
  border-radius: var(--border-radius);
  background-color: var(--white);
  z-index: 1;
  pointer-events: auto;
  box-shadow: 0 2px 4px 0 rgba(var(--color-text-rgb), 10%);

  &:not(.DateInput) {
    width: ${(props) => (props.$hasUnit ? "110px" : "90px")};

    .input {
      border-radius: var(--border-radius);
    }
  }

  &.DateInput {
    &.DayDateInput {
      width: 120px;
    }
  }

  & > .rdt {
    position: static;
    width: 0px;
  }
`;

const DateInputWrapper = styled.div`
  border-radius: var(--border-radius);

  .input {
    padding: 4px 0;
    background: transparent;
    width: 100%;
    border: none;
  }
`;

const UnitLabel = styled.span`
  color: var(--color-text);
  font-size: 12px;
  white-space: nowrap;
  user-select: none;
  pointer-events: none;
  padding-right: 4px;
`;
